import type {
  AddPageOptions,
  CreateDocumentOptions,
  CroppingConfiguration,
  DocumentQualityAnalyzerConfiguration,
  DocumentScannerConfiguration,
  DocumentScanningFlow,
  ImageInput,
  ModifyPageOptions,
  Page,
  ResultWrapper,
} from '../types';
import {
  DocumentData,
  DocumentQualityAnalyzerResult,
  DocumentScanningResult,
  handleImageInput,
  mapRTUUIResult,
  withSBErrorHandling,
} from '../types';

import { ScanbotSDKPlugin } from './Definitions';

/**
 * @internal
 * @hidden
 */
export const ScanbotDocumentImpl = {
  async createDocumentFromImages(params: {
    images?: ImageInput[];
    options?: CreateDocumentOptions;
  }): Promise<DocumentData> {
    return withSBErrorHandling(
      async () =>
        new DocumentData(
          await ScanbotSDKPlugin.createDocumentFromImages({
            ...params,
            images: Array.isArray(params.images) ? handleImageInput(params.images) : undefined,
          }),
        ),
    );
  },

  async createDocumentFromLegacyPages(params: {
    pages: Page[];
    documentImageSizeLimit?: number;
  }): Promise<DocumentData> {
    return withSBErrorHandling(
      async () => new DocumentData(await ScanbotSDKPlugin.createDocumentFromLegacyPages(params)),
    );
  },

  async createDocumentFromPdf(params: { pdfFileUri: string; options?: CreateDocumentOptions }): Promise<DocumentData> {
    return withSBErrorHandling(async () => new DocumentData(await ScanbotSDKPlugin.createDocumentFromPdf(params)));
  },

  async documentExists(documentUuid: string): Promise<boolean> {
    return withSBErrorHandling(async () => {
      return (await ScanbotSDKPlugin.documentExists({ documentUuid: documentUuid })).result;
    });
  },

  async loadDocument(documentUuid: string): Promise<DocumentData> {
    return withSBErrorHandling(
      async () => new DocumentData(await ScanbotSDKPlugin.loadDocument({ documentUuid: documentUuid })),
    );
  },

  async getStoredDocumentUuids(): Promise<string[]> {
    return withSBErrorHandling(async () => {
      return (await ScanbotSDKPlugin.getStoredDocumentUuids()).result;
    });
  },

  async cloneDocument(documentUuid: string): Promise<DocumentData> {
    return withSBErrorHandling(
      async () => new DocumentData(await ScanbotSDKPlugin.cloneDocument({ documentUuid: documentUuid })),
    );
  },

  async deleteDocument(documentUuid: string): Promise<void> {
    return withSBErrorHandling(() => ScanbotSDKPlugin.deleteDocument({ documentUuid: documentUuid }));
  },

  async deleteAllDocuments(): Promise<void> {
    return withSBErrorHandling(() => ScanbotSDKPlugin.deleteAllDocuments());
  },

  async addPages(params: {
    documentUuid: string;
    images: ImageInput[];
    options?: AddPageOptions;
  }): Promise<DocumentData> {
    return withSBErrorHandling(
      async () =>
        new DocumentData(await ScanbotSDKPlugin.addPages({ ...params, images: handleImageInput(params.images) })),
    );
  },

  async movePage(params: { documentUuid: string; fromIndex: number; toIndex: number }): Promise<DocumentData> {
    return withSBErrorHandling(async () => new DocumentData(await ScanbotSDKPlugin.movePage(params)));
  },

  async modifyPage(params: {
    documentUuid: string;
    pageUuid: string;
    options?: ModifyPageOptions;
  }): Promise<DocumentData> {
    return withSBErrorHandling(async () => new DocumentData(await ScanbotSDKPlugin.modifyPage(params)));
  },

  async removePages(params: { documentUuid: string; pageUuids: string[] }): Promise<DocumentData> {
    return withSBErrorHandling(async () => new DocumentData(await ScanbotSDKPlugin.removePagesFromDocument(params)));
  },

  async removeAllPages(documentUuid: string): Promise<DocumentData> {
    return withSBErrorHandling(
      async () => new DocumentData(await ScanbotSDKPlugin.removeAllPages({ documentUuid: documentUuid })),
    );
  },

  async scanFromImage(params: {
    image: ImageInput;
    configuration: DocumentScannerConfiguration;
  }): Promise<DocumentScanningResult> {
    return withSBErrorHandling(
      async () =>
        new DocumentScanningResult(
          await ScanbotSDKPlugin.scanDocumentFromImage({
            configuration: params.configuration,
            image: handleImageInput(params.image),
          }),
        ),
    );
  },

  async analyzeQualityOnImage(params: {
    image: ImageInput;
    configuration: DocumentQualityAnalyzerConfiguration;
  }): Promise<DocumentQualityAnalyzerResult> {
    return withSBErrorHandling(
      async () =>
        new DocumentQualityAnalyzerResult(
          await ScanbotSDKPlugin.analyzeDocumentQualityOnImage({
            configuration: params.configuration,
            image: handleImageInput(params.image),
          }),
        ),
    );
  },

  async startScanner(configuration: DocumentScanningFlow): Promise<ResultWrapper<DocumentData>> {
    return withSBErrorHandling(async () =>
      mapRTUUIResult<DocumentData>(await ScanbotSDKPlugin.startDocumentScanner(configuration), DocumentData),
    );
  },

  async startCroppingScreen(configuration: CroppingConfiguration): Promise<ResultWrapper<DocumentData>> {
    return withSBErrorHandling(async () =>
      mapRTUUIResult<DocumentData>(await ScanbotSDKPlugin.startCroppingScreen(configuration), DocumentData),
    );
  },
};
